<template>
  <div data-test-id="form-lookup">
    <slot name="heading">
      <h2 v-if="$t.simplifiedEnrollment.lookupHeading" class="mb-2 text-center" :class="brandClasses.heading">
        {{ $t.simplifiedEnrollment.lookupHeading }}
      </h2>
      <p v-if="$feature.enableLoyaltyFeatures && $t.simplifiedEnrollment.lookupDescription" class="mb-2 text-center" :class="brandClasses.description">
        {{ $t.simplifiedEnrollment.lookupDescription }}
      </p>
    </slot>
    <lazy-form-sign-in-social-login
      v-if="$feature.configSocialLogin.length && !showSocialLoginAfterLookupForm"
      :form-location
      simplified
    />
    <div
      v-if="$feature.configSignUp?.loyaltyRewardPercent"
      class="mb-8 mt-6 flex items-center gap-x-3 b b-grey-70 rounded-lg bg-grey-90 p-4"
    >
      <vf-icon size="xl" src="/img/loyalty/loyalty-discount.svg" />
      <span class="body-2">
        {{ replaceAll($t.simplifiedEnrollment.lookupPromo, { percent: $feature.configSignUp.loyaltyRewardPercent }) }}
      </span>
    </div>
    <base-form class="mt-4" :form @submit="submit">
      <vf-form-field
        v-slot="{ attrs }"
        name="email"
        :rule="[validateRequired($t.email), validateEmail(), validateMaxLength(100)]"
      >
        <input-email v-model="form.email" v-bind="attrs" />
      </vf-form-field>
      <vf-form-field
        v-if="$feature.configSocialLogin.length && toggleSignInTermsCheckbox"
        v-slot="{ attrs }"
        name="acceptedTerms"
        :rule="validateRequired($t.validators.requiredCheckbox)"
      >
        <vf-checkbox v-model="form.acceptedTerms" class="mt-4 ws-pre-wrap" v-bind="attrs" required>
          <form-legal-disclaimer
            :content="$t.brandAgreement.signInTermsAndConditions"
            :data-test-ids="{ 'loyalty-terms': 'form-sign-up-link-loyalty' }"
          />
        </vf-checkbox>
      </vf-form-field>
      <vf-button class="my-6 w-full" :loading type="submit">
        {{ $t.continueWithEmail }}
      </vf-button>
      <template v-if="$feature.configSocialLogin.length && showSocialLoginAfterLookupForm">
        <div class="mb-8 mt-2 flex items-center capitalize">
          <hr class="grow b-grey-70">
          <span class="mx-2 shrink">{{ $t.or }}</span>
          <hr class="grow b-grey-70">
        </div>
        <lazy-form-sign-in-social-login :form-location simplified />
      </template>
      <form-legal-disclaimer
        v-else-if="$feature.configSocialLogin.length && !toggleSignInTermsCheckbox"
        :content="$t.brandAgreement.passiveTermsConditionsAndPolicy"
        :data-test-ids="{ 'loyalty-terms': 'form-sign-up-link-loyalty' }"
      />
    </base-form>
  </div>
</template>

<script lang="ts" setup>
import type { LookupData } from '#root/api/clients/consumer/data-contracts'
import type { FormLocation } from '#types/gtm'

const { email = '', formLocation } = defineProps<{
  email?: string
  formLocation: FormLocation
}>()

const emit = defineEmits<{
  submit: [email: string, lookupData?: LookupData]
}>()

const { consumer } = useApi()
const { $feature, $gtm } = useNuxtApp()
const {
  brandClasses,
  showSignInTermsCheckbox,
  showSocialLoginAfterLookupForm,
} = useAppConfig().components.dialog.simplifiedEnrollment
const { validateEmail, validateMaxLength, validateRequired } = useLocalizedValidators()
const recaptcha = useRecaptcha()
const locale = useLocale()

const loading = ref(false)

const form = reactive({
  email,
  acceptedTerms: false
})

const toggleSignInTermsCheckbox = computed(() => showSignInTermsCheckbox?.includes(locale))

const submit = async () => {
  try {
    loading.value = true
    const token = await recaptcha.execute('lookup')
    try {
      $gtm.push('authForm.onEmail', 'Login', formLocation)
      $gtm.push('authForm.onEmail', 'Gateway', formLocation, true)

      const lookupData = await consumer.$lookup({ email: form.email, recaptcha_response: token })
      emit('submit', form.email, lookupData)
    }
    catch (e: any) {
      if (e.response.status === 404)
        emit('submit', form.email)
    }
  }
  catch (err) {
    assertApiError(err)
  }
  finally {
    loading.value = false
  }
}
</script>
